import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Circle, Marker, Popup, useMap } from 'react-leaflet';
import { MapPin, Users, RefreshCw, Maximize2 } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom marker icon based on user count
const createCustomIcon = (count, color) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        background-color: ${color};
        color: white;
        border-radius: 50%;
        width: 36px;
        height: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        font-size: 12px;
        border: 3px solid white;
        box-shadow: 0 2px 6px rgba(0,0,0,0.3);
      ">
        ${count}
      </div>
    `,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
  });
};

// Component to fit map bounds
const FitBounds = ({ locations }) => {
  const map = useMap();

  useEffect(() => {
    if (locations && locations.length > 0) {
      const validLocations = locations.filter(l => l.latitude && l.longitude);
      if (validLocations.length > 0) {
        const bounds = L.latLngBounds(
          validLocations.map(l => [l.latitude, l.longitude])
        );
        map.fitBounds(bounds, { padding: [50, 50] });
      }
    }
  }, [locations, map]);

  return null;
};

/**
 * Location Heatmap Widget - Shows location-wise user distribution on map
 */
const LocationHeatmap = ({ locations, loading, onRefresh, onLocationClick }) => {
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Default center (India)
  const defaultCenter = [20.5937, 78.9629];
  const defaultZoom = 5;

  // Calculate center from locations
  const mapCenter = locations && locations.length > 0
    ? [
        locations.reduce((sum, l) => sum + (l.latitude || 0), 0) / locations.length,
        locations.reduce((sum, l) => sum + (l.longitude || 0), 0) / locations.length,
      ]
    : defaultCenter;

  const getLocationColor = (location) => {
    const total = location.total_count || 0;
    if (total === 0) return '#9ca3af'; // gray
    if (total <= 5) return '#22c55e'; // green
    if (total <= 15) return '#3b82f6'; // blue
    if (total <= 30) return '#f59e0b'; // amber
    return '#ef4444'; // red for high density
  };

  const getCircleRadius = (location) => {
    const baseRadius = location.radius || 100;
    const total = location.total_count || 0;
    // Scale radius slightly based on user count
    return Math.max(baseRadius, baseRadius + (total * 5));
  };

  // Summary stats
  const totalUsers = locations?.reduce((sum, l) => sum + (l.total_count || 0), 0) || 0;
  const activeLocations = locations?.filter(l => (l.total_count || 0) > 0).length || 0;

  if (loading) {
    return (
      <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
        <div className="animate-pulse">
          <div className="h-5 bg-gray-200 rounded w-1/3 mb-3"></div>
          <div className="h-48 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-100 ${isFullscreen ? 'fixed inset-4 z-50' : ''}`}>
      {/* Header */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPin size={18} className="text-blue-600" />
            <h3 className="text-base font-semibold text-gray-800">Location Heatmap</h3>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onRefresh}
              className="p-1.5 hover:bg-gray-100 rounded transition-colors"
              title="Refresh"
            >
              <RefreshCw size={14} className="text-gray-600" />
            </button>
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="p-1.5 hover:bg-gray-100 rounded transition-colors"
              title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
            >
              <Maximize2 size={14} className="text-gray-600" />
            </button>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="flex items-center gap-2 mt-2 text-sm">
          <span className="flex items-center gap-1.5 text-gray-600">
            <Users size={14} />
            {totalUsers} users across {activeLocations} locations
          </span>
        </div>
      </div>

      {/* Map */}
      <div className={`${isFullscreen ? 'h-[calc(100%-180px)]' : 'h-48'}`}>
        {locations && locations.length > 0 ? (
          <MapContainer
            center={mapCenter}
            zoom={defaultZoom}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FitBounds locations={locations} />

            {locations.map((location) => {
              if (!location.latitude || !location.longitude) return null;

              const color = getLocationColor(location);
              const radius = getCircleRadius(location);

              return (
                <React.Fragment key={location.location_id}>
                  {/* Geofence circle */}
                  <Circle
                    center={[location.latitude, location.longitude]}
                    radius={radius}
                    pathOptions={{
                      color: color,
                      fillColor: color,
                      fillOpacity: 0.2,
                      weight: 2,
                    }}
                  />

                  {/* Marker with user count */}
                  <Marker
                    position={[location.latitude, location.longitude]}
                    icon={createCustomIcon(location.total_count || 0, color)}
                    eventHandlers={{
                      click: () => {
                        setSelectedLocation(location);
                        onLocationClick?.(location);
                      },
                    }}
                  >
                    <Popup>
                      <div className="p-2 min-w-[150px]">
                        <h4 className="font-semibold text-gray-800">
                          {location.location_name}
                        </h4>
                        <div className="mt-2 space-y-1 text-sm">
                          <p className="flex items-center justify-between">
                            <span className="text-gray-1000">Checked In:</span>
                            <span className="font-medium text-green-600">
                              {location.checked_in_count || 0}
                            </span>
                          </p>
                          <p className="flex items-center justify-between">
                            <span className="text-gray-1000">On Break:</span>
                            <span className="font-medium text-yellow-600">
                              {location.on_break_count || 0}
                            </span>
                          </p>
                          <p className="flex items-center justify-between border-t pt-1 mt-1">
                            <span className="text-gray-1000">Total:</span>
                            <span className="font-bold">{location.total_count || 0}</span>
                          </p>
                        </div>
                        <p className="text-xs text-gray-400 mt-2">
                          Radius: {location.radius || 100}m
                        </p>
                      </div>
                    </Popup>
                  </Marker>
                </React.Fragment>
              );
            })}
          </MapContainer>
        ) : (
          <div className="h-full flex items-center justify-center bg-gray-50">
            <div className="text-center text-gray-1000">
              <MapPin size={40} className="mx-auto mb-2 opacity-50" />
              <p>No locations configured</p>
            </div>
          </div>
        )}
      </div>

      {/* Location List */}
      <div className="p-4 border-t border-gray-100 max-h-36 overflow-y-auto">
        <p className="text-xs font-medium text-gray-600 mb-2">Location Summary</p>
        <div className="space-y-2">
          {locations?.map((location) => (
            <div
              key={location.location_id}
              className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors ${
                selectedLocation?.location_id === location.location_id
                  ? 'bg-blue-50 border border-blue-200'
                  : 'hover:bg-gray-50'
              }`}
              onClick={() => setSelectedLocation(location)}
            >
              <div className="flex items-center gap-2">
                <span
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: getLocationColor(location) }}
                ></span>
                <span className="text-sm font-medium text-gray-700">
                  {location.location_name}
                </span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <span className="text-green-600">{location.checked_in_count || 0} in</span>
                <span className="text-yellow-600">{location.on_break_count || 0} break</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LocationHeatmap;
